import { useEffect, useRef, useState } from "react"
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  Radio,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import { Close } from "@mui/icons-material"
import { enqueueSnackbar } from "notistack"
import { store } from "@core/store"
import { useInfiniteSelectList } from "@/core/hooks"
import { AdvertisingContent } from "../../interfaces"
import { useAdvWriteAccess } from "../../hooks/useAdvWriteAccess"
import { CONTENTS_LIMIT_DEFAULT, contentsApi } from "../../store"
import { useCreateBindingMutation } from "../../store/contentsApi"
import { contentErrorMessage } from "../../utils/contentErrors"
import { buttonsCount, filesSummaryText } from "../../utils/contentsFormatting"
import { formatContentDateTime } from "../../utils/formatContentDateTime"

export interface BindMaterialDialogProps {
  open: boolean
  orderId: number
  onClose: () => void
}

// Закрытие и смена ордера завершают сеанс вместе с его выбором и обработчиками.
export const BindMaterialDialog = (props: BindMaterialDialogProps) =>
  props.open && Number.isSafeInteger(props.orderId) && props.orderId > 0 ? (
    <BindMaterialSession key={props.orderId} {...props} />
  ) : null

const BindMaterialSession = ({ orderId, onClose }: BindMaterialDialogProps) => {
  const canWrite = useAdvWriteAccess()
  const [search, setSearch] = useState("")
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const lock = useRef(false)
  const live = useRef(true)
  const allowed = useRef(canWrite)
  allowed.current = canWrite
  const [createBinding] = useCreateBindingMutation()
  const list = useInfiniteSelectList<AdvertisingContent>({
    fetchPage: async ({ limit, offset }) => {
      const request = store.dispatch(
        contentsApi.endpoints.getContents.initiate(
          { queryParams: { limit, offset } },
          { forceRefetch: true },
        ),
      )
      try {
        const result = await request.unwrap()
        return { items: result.items, total: result.count }
      } finally {
        request.unsubscribe()
      }
    },
    getId: (item) => item.id,
    pageSize: CONTENTS_LIMIT_DEFAULT,
  })
  useEffect(() => {
    live.current = true
    return () => {
      live.current = false
    }
  }, [])

  const query = search.trim().toLowerCase()
  const visible = list.items.filter(
    (item) =>
      (item.text ?? "").toLowerCase().includes(query) ||
      item.assets.some((asset) => asset.filename.toLowerCase().includes(query)),
  )
  const close = () => {
    if (!lock.current) onClose()
  }
  const submit = async () => {
    if (
      !allowed.current ||
      lock.current ||
      selectedId === null ||
      !list.items.some((item) => item.id === selectedId)
    )
      return
    lock.current = true
    setPending(true)
    setError(null)
    try {
      const result = await createBinding({
        pathParams: { contentId: selectedId },
        body: { order_id: orderId },
      }).unwrap()
      if (!live.current) return
      enqueueSnackbar(
        result.existing ? "Материал уже был привязан" : "Материал привязан",
        { variant: result.existing ? "info" : "success" },
      )
      onClose()
    } catch (reason) {
      if (live.current) setError(contentErrorMessage(reason))
    } finally {
      lock.current = false
      if (live.current) setPending(false)
    }
  }
  return (
    <Dialog
      open
      fullWidth
      maxWidth="sm"
      onClose={close}
      disableEscapeKeyDown={pending}
    >
      <DialogTitle>
        Добавить существующий материал
        {!pending && (
          <IconButton
            aria-label="close"
            onClick={close}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1} sx={{ pt: 1 }}>
          <TextField
            label="Поиск по тексту или имени файла"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            disabled={pending}
            helperText="Поиск только среди загруженных материалов. Чтобы искать дальше, загрузите ещё."
          />
          {error && <Alert severity="error">{error}</Alert>}
          {list.isFetching && (
            <LinearProgress aria-label="Загрузка материалов" />
          )}
          {list.error != null && (
            <Alert
              severity="error"
              action={
                <Button
                  onClick={list.items.length ? list.loadMore : list.reload}
                  disabled={pending || list.isFetching}
                >
                  Повторить
                </Button>
              }
            >
              Не удалось загрузить материалы
            </Alert>
          )}
          {!list.isFetching && list.error == null && visible.length === 0 && (
            <Typography>
              {list.items.length === 0
                ? "Пока нет материалов"
                : "Среди загруженных материалов ничего не найдено"}
            </Typography>
          )}
          <List disablePadding aria-label="Материалы">
            {visible.map((item) => (
              <ListItem key={item.id} disableGutters>
                <Radio
                  name="material"
                  inputProps={{ "aria-label": `Материал #${item.id}` }}
                  checked={selectedId === item.id}
                  onChange={() => setSelectedId(item.id)}
                  disabled={pending || !canWrite}
                />
                <Box minWidth={0}>
                  <Typography
                    sx={{
                      whiteSpace: "pre-wrap",
                      display: "-webkit-box",
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {item.text ?? "Без текста"}
                  </Typography>
                  <Typography variant="body2">
                    {filesSummaryText(item)}
                  </Typography>
                  <Typography variant="caption">
                    Кнопки: {buttonsCount(item)} ·{" "}
                    {formatContentDateTime(item.created_at)}
                  </Typography>
                </Box>
              </ListItem>
            ))}
          </List>
          {list.hasMore && (
            <Button
              onClick={list.loadMore}
              disabled={pending || list.isFetching}
            >
              Загрузить ещё
            </Button>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={pending} onClick={close}>
          Отмена
        </Button>
        {canWrite && (
          <Button
            variant="contained"
            disabled={pending || selectedId === null}
            onClick={() => void submit()}
          >
            Привязать
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
